/**
 * Central logger for the adhoc booth display app.
 * - Mirrors to browser console
 * - Buffers in sessionStorage (survives refresh within tab)
 * - Batches events to POST /adhoc/session/:id/events
 */

import { API_BASE } from '../api/config';

export type AdhocLogLevel = 'debug' | 'info' | 'warn' | 'error';
export type AdhocLogSource = 'app' | 'scan' | 'api';

export interface AdhocLogEntry {
  ts: string;
  level: AdhocLogLevel;
  source: AdhocLogSource;
  message: string;
  meta?: Record<string, unknown>;
}

const BUFFER_KEY = 'adhoc_debug_log';
const MAX_BUFFER = 200;
const FLUSH_DELAY_MS = 2000;
const MAX_BATCH = 25;

const PII_KEYS = new Set([
  'email',
  'guest_name',
  'guestName',
  'vitals',
  'results',
  'rawResults',
  'vitalSignsResults',
  'password',
  'token',
]);

let sessionId: string | null = null;
let adhocId: string | null = null;
const pending: AdhocLogEntry[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushing = false;

function sanitizeMeta(meta?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!meta) return undefined;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(meta)) {
    if (PII_KEYS.has(key)) continue;
    if (value === undefined) continue;
    out[key] = value;
  }
  return Object.keys(out).length ? out : undefined;
}

function readBuffer(): AdhocLogEntry[] {
  try {
    const raw = sessionStorage.getItem(BUFFER_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as AdhocLogEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeBuffer(entries: AdhocLogEntry[]) {
  try {
    sessionStorage.setItem(BUFFER_KEY, JSON.stringify(entries.slice(-MAX_BUFFER)));
  } catch {
    /* quota exceeded */
  }
}

function appendToBuffer(entry: AdhocLogEntry) {
  writeBuffer([...readBuffer(), entry]);
}

function consoleEmit(entry: AdhocLogEntry) {
  const prefix = `[Adhoc:${entry.source}]`;
  const payload = entry.meta ? [prefix, entry.message, entry.meta] : [prefix, entry.message];
  switch (entry.level) {
    case 'error':
      console.error(...payload);
      break;
    case 'warn':
      console.warn(...payload);
      break;
    case 'debug':
      console.debug(...payload);
      break;
    default:
      console.log(...payload);
  }
}

async function flushToServer() {
  if (!sessionId || pending.length === 0 || flushing) return;
  flushing = true;
  const batch = pending.splice(0, MAX_BATCH);
  const url = `${API_BASE}/adhoc/session/${encodeURIComponent(sessionId)}/events`;
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': '1',
      },
      body: JSON.stringify({ events: batch }),
    });
    if (!res.ok && res.status !== 204) {
      pending.unshift(...batch);
    }
  } catch {
    pending.unshift(...batch);
  } finally {
    flushing = false;
    if (pending.length > 0) {
      scheduleFlush();
    }
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushToServer();
  }, FLUSH_DELAY_MS);
}

/** Bind the active guest session (call when sessionId is known). */
export function setAdhocLogSession(id: string | null, boothId?: string | null) {
  sessionId = id;
  if (boothId !== undefined) adhocId = boothId;
  if (id) {
    adhocLog('app', 'info', 'Session bound for logging', { sessionId: id, adhocId });
    void flushToServer();
  }
}

/** Bind booth id without a session (landing page). */
export function setAdhocLogBooth(boothId: string) {
  adhocId = boothId;
}

export function adhocLog(
  source: AdhocLogSource,
  level: AdhocLogLevel,
  message: string,
  meta?: Record<string, unknown>,
) {
  const entry: AdhocLogEntry = {
    ts: new Date().toISOString(),
    level,
    source,
    message,
    meta: sanitizeMeta(meta),
  };

  consoleEmit(entry);
  appendToBuffer(entry);
  pending.push(entry);

  if (level === 'error') {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    void flushToServer();
  } else {
    scheduleFlush();
  }
}

/** All buffered entries (for staff download). */
export function getAdhocLogBuffer(): AdhocLogEntry[] {
  return readBuffer();
}

/** Download buffered client log as a text file. */
export function downloadAdhocDebugLog(filename?: string) {
  const lines = readBuffer().map((e) => {
    const meta = e.meta ? ` ${JSON.stringify(e.meta)}` : '';
    return `${e.ts} | ${e.level.toUpperCase()} | ${e.source} | ${e.message}${meta}`;
  });
  const header = [
    '# MyWellfie Adhoc client debug log',
    `# session=${sessionId ?? 'none'} booth=${adhocId ?? 'unknown'}`,
    `# exported=${new Date().toISOString()}`,
    '',
  ];
  const blob = new Blob([header.concat(lines).join('\n')], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename ?? `adhoc-log-${sessionId?.slice(0, 8) ?? 'booth'}-${Date.now()}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Clear client buffer (e.g. on reset to landing). */
export function clearAdhocLogBuffer() {
  pending.length = 0;
  try {
    sessionStorage.removeItem(BUFFER_KEY);
  } catch {
    /* ignore */
  }
}
